import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, X } from 'lucide-react';
import { doc, onSnapshot, type Timestamp } from 'firebase/firestore';
import { db, appId } from '@/config/firebase';
import { useUpdateAnimation } from '@/context/UpdateAnimationContext';

/**
 * Key for storing the last seen update hint timestamp in localStorage.
 */
const LAST_SEEN_KEY = 'spirit_update_hint_last_seen';

/**
 * Key for storing dismissed state in sessionStorage (resets on new session).
 */
const DISMISSED_KEY = 'spirit_update_hint_dismissed';

/**
 * A subtle, non-intrusive badge that appears at the bottom-left when
 * an admin suggests users refresh their browser.
 *
 * Positioned just above the navigation bar to avoid interfering with
 * content or input fields.
 */
export function UpdateBadge() {
  const [showBadge, setShowBadge] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const lastToastTimestamp = useRef<number>(0);
  const { triggerRefresh } = useUpdateAnimation();

  useEffect(() => {
    // Check if already dismissed in this session
    const dismissed = sessionStorage.getItem(DISMISSED_KEY);
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      // Only consider dismissed if it was recent (same update)
      const lastSeen = localStorage.getItem(LAST_SEEN_KEY);
      if (lastSeen && dismissedTime >= parseInt(lastSeen, 10)) {
        setIsDismissed(true);
      }
    }

    // Reference to the update_hint config document
    const updateHintRef = doc(db, 'artifacts', appId, 'config', 'update_hint');

    // Subscribe to real-time updates
    const unsubscribe = onSnapshot(
      updateHintRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setShowBadge(false);
          return;
        }

        const data = snapshot.data();
        const updatedAt = data?.updatedAt as Timestamp | undefined;

        if (!updatedAt) {
          setShowBadge(false);
          return;
        }

        // Get the timestamp in milliseconds
        const updateTimestamp = updatedAt.toMillis();

        // Get last seen timestamp from localStorage
        const lastSeen = localStorage.getItem(LAST_SEEN_KEY);
        const lastSeenTimestamp = lastSeen ? parseInt(lastSeen, 10) : 0;

        // Check if this is a new update hint
        if (updateTimestamp > lastSeenTimestamp && updateTimestamp > lastToastTimestamp.current) {
          // Store the new timestamp
          localStorage.setItem(LAST_SEEN_KEY, updateTimestamp.toString());
          lastToastTimestamp.current = updateTimestamp;

          // Clear any previous dismissal since this is a new update
          sessionStorage.removeItem(DISMISSED_KEY);
          setIsDismissed(false);

          // Show the badge
          setShowBadge(true);
        }
      },
      (error) => {
        console.warn('Update hint listener error:', error);
      }
    );

    return () => {
      unsubscribe();
    };
  }, []);

  const handleRefresh = () => {
    // Trigger refresh with update animation
    triggerRefresh();
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDismissed(true);
    // Store dismissal with current timestamp
    const lastSeen = localStorage.getItem(LAST_SEEN_KEY);
    if (lastSeen) {
      sessionStorage.setItem(DISMISSED_KEY, lastSeen);
    }
  };

  // Don't show if dismissed or no update available
  if (!showBadge || isDismissed) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        className="fixed bottom-20 left-4 z-[90]"
        initial={{ opacity: 0, x: -20, scale: 0.9 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        exit={{ opacity: 0, x: -20, scale: 0.9 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
      >
        <motion.div
          className="relative"
          animate={{
            scale: [1, 1.02, 1],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          {/* Main Badge */}
          <div
            role="button"
            tabIndex={0}
            onClick={handleRefresh}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleRefresh();
              }
            }}
            className="group flex items-center gap-2 bg-white/95 backdrop-blur-sm text-slate-700 rounded-full shadow-lg border border-slate-200/80 hover:shadow-xl hover:border-[#8e4585]/30 transition-all cursor-pointer pl-3 pr-4 py-2"
          >
            {/* Icon */}
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-[#8e4585] to-[#a855a0]">
              <RefreshCw size={16} className="text-white" />
            </div>

            {/* Message */}
            <div className="text-sm">
              <span className="font-medium text-slate-700">Update available</span>
              <span className="text-slate-500 ml-1">· Tap to refresh</span>
            </div>
          </div>

          {/* Dismiss Button */}
          <button
            onClick={handleDismiss}
            className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center bg-white text-slate-400 rounded-full shadow-md border border-slate-200 hover:bg-slate-100 hover:text-slate-600 transition-colors z-10"
            aria-label="Dismiss"
          >
            <X size={12} />
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
